import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Title,
  Text,
  PrimaryButton,
  RowTable,
  SlimRowTable,
} from '@src/shared/ui';
import { BottomSheetHeader } from '../bottom-sheet-header';
import { FlightDetails } from '../flight-details';
import {
  useSelectedLoanSimulationState,
  useProposalState,
  useRemoteConfigFlag,
} from '@src/shared/hooks';
import { REMOTE_CONFIG_FLAGS } from '@src/shared/zustand/remote-config/remote-config.constants';
import {
  formatBRDate,
  formatBR,
  getDownPaymentPlusInstallment,
  formatMapFlights,
} from '@src/shared/utils';
import { LoanSimulation } from '@src/shared/models';
import { useResponsiveFlex } from '@src/shared/hooks';

const Installments = ({
  loanSimulation,
}: {
  loanSimulation: LoanSimulation;
}) => {
  return (
    <Box
      className={`flex justify-between ${useResponsiveFlex({ label: 'Parcelas' })}`}
    >
      <Text className='text-base text-neutral-70'>Parcelas:</Text>
      <Text className='text-base font-semibold text-neutral-70'>
        {loanSimulation ? getDownPaymentPlusInstallment(loanSimulation) : ''}
      </Text>
    </Box>
  );
};

type BottomSheetProps = {
  isOpen?: boolean;
  close: VoidFunction;
  showFlights?: boolean;
  showTaxes?: boolean;
  showInstallments?: boolean;
  className?: string;
};

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  close,
  showFlights = true,
  showTaxes = true,
  showInstallments = true,
  className = '',
}) => {
  const bodyRef = useRef(null);

  const { loanSimulation } = useSelectedLoanSimulationState();
  const { proposal, loading } = useProposalState();
  const { value: showFlightDetails } = useRemoteConfigFlag(
    REMOTE_CONFIG_FLAGS.SHOW_FLIGHT_DETAILS,
    false
  );

  const flights = formatMapFlights({ loading, proposal });

  const handleScrollToBody = () => {
    bodyRef?.current?.scrollIntoView();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          onClick={handleScrollToBody}
          onScrollCapture={handleScrollToBody}
          onScroll={handleScrollToBody}
          className={`fixed h-[100vh] w-full z-50 inset-0 bg-[#000] bg-opacity-70 flex justify-center items-end ${className}`}
        >
          <motion.div
            onPan={handleScrollToBody}
            onScrollCapture={handleScrollToBody}
            onScroll={handleScrollToBody}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 200, damping: 25 }}
            className={`fixed b-0 ${showFlightDetails ? 'pb-[34%]' : 'pb-[24%]'} xs:pb-[25%] overflow-auto w-full min-w-full bg-white rounded-t-2xl mx-6 shadow-[0px_-4px_30px_0px_rgba(0,0,0,0.07)] ${showFlightDetails ? 'h-[95vh]' : ''}`}
          >
            <BottomSheetHeader close={close} onClick={handleScrollToBody} />
            <div
              onClick={handleScrollToBody}
              onScroll={handleScrollToBody}
              onScrollCapture={handleScrollToBody}
              className='flex flex-col mx-6 sm:mx-10 mt-[64px]'
            >
              <Title
                className={`font-bold text-neutral-70 text-[20px] sm:text-[28px] ${showFlightDetails ? '' : 'pb-6'}`}
              >
                Detalhes da sua compra:
              </Title>

              {showFlightDetails && showFlights && flights?.length > 0 && (
                <div className='flex-1 rounded-2xl bg-neutral-10 my-6 sm:my-8 p-6 sm:p-8'>
                  {flights.map((flight, index) => (
                    <FlightDetails
                      loading={loading}
                      key={index}
                      title={flight.title}
                      from={flight.from}
                      to={flight.to}
                      only={flight.only}
                      isReturn={flight.isReturn}
                      isModal
                    />
                  ))}
                </div>
              )}

              <Installments loanSimulation={loanSimulation} />
              {showTaxes && (
                <Box className='flex flex-col pb-6 border-b-[1px] border-[#E1E4E6]'>
                  <SlimRowTable
                    className='mt-4 sm:mt-3'
                    label='Valor da viagem'
                    value={formatBR(loanSimulation.totalPriceOfFlightTicket)}
                  />
                  <SlimRowTable
                    className='mt-3 sm:mt-1'
                    label='IOF'
                    value={formatBR(loanSimulation.iofAmount)}
                  />
                  <SlimRowTable
                    className='mt-3 sm:mt-1'
                    label='Taxa de juros'
                    value={`${loanSimulation.interestRate}% Mês`}
                  />
                  <SlimRowTable
                    className='mt-3 sm:mt-1'
                    label='Custo efetivo total'
                    value={`${loanSimulation.monthlyEffectiveInterestRate?.toFixed(2)}% Mês`}
                  />
                  <SlimRowTable
                    className='mt-3 sm:mt-1'
                    label='Taxa cadastral'
                    value={formatBR(loanSimulation.tacAmount)}
                  />
                </Box>
              )}
              {showInstallments && (
                <>
                  <RowTable
                    label='Primeiro vencimento'
                    value={formatBRDate(loanSimulation.downPaymentDueDate)}
                  />
                  <RowTable
                    label='Próximos vencimentos'
                    value={`Todo dia ${loanSimulation.schedule[0].paymentDate.split('-')[2]}`}
                  />
                  <RowTable
                    md
                    label='Total'
                    value={formatBR(
                      loanSimulation.totalAmountOwedWithDownPayment
                    )}
                    notBorder={true}
                  />
                </>
              )}
              <PrimaryButton
                onClick={close}
                className={`
                  mt-8
                  py-5
                  flex-1
                  min-w-auto
                `}
              >
                Fechar detalhes
              </PrimaryButton>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
